

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = async () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

export const api = {
  // Events
  getEvents: async (params?: { category?: string; search?: string; is_virtual?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    if (params?.is_virtual !== undefined) query.set('is_virtual', String(params.is_virtual));
    
    const response = await fetch(`${BACKEND_URL}/api/events?${query}`);
    return response.json();
  },

  getEvent: async (eventId: string) => {
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`);
    return response.json();
  },

  getSuggestions: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/suggestions`, { headers });
    return response.json();
  },

  // Tickets
  getTicketTypes: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/ticket-types`, { headers });
    if (!response.ok) throw new Error('Failed to get ticket types');
    return response.json();
  },

  purchaseTicket: async (eventId: string, ticketType: string = 'general') => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/tickets?event_id=${eventId}&ticket_type=${ticketType}`,
      { method: 'POST', headers }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to purchase ticket');
    }
    return response.json();
  },

  getMyTickets: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/tickets`, { headers });
    return response.json();
  },

  // Messages
  sendMessage: async (eventId: string, content: string, mediaUrl?: string, mediaType?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_id: eventId, content, media_url: mediaUrl, media_type: mediaType })
    });
    return response.json();
  },

  getMessages: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/messages/${eventId}`, { headers });
    return response.json();
  },

  // Vibe Match
  findVibeMatches: async (eventId?: string) => {
    const headers = await getAuthHeaders();
    const url = eventId
      ? `${BACKEND_URL}/api/vibe/match?event_id=${eventId}`
      : `${BACKEND_URL}/api/vibe/match`;
    const response = await fetch(url, { method: 'POST', headers });
    return response.json();
  },

  getVibeMatches: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/vibe/matches`, { headers });
    if (!response.ok) return [];
    return response.json();
  },

  getMyMatches: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/vibe/matches`, { headers });
    return response.json();
  },

  respondToMatch: async (matchId: string, status: 'accepted' | 'rejected') => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/vibe/matches/${matchId}/respond`, {
      method: 'POST', 
      headers,
      body: JSON.stringify({ response: status === 'accepted' ? 'accept' : 'decline' })
    });
    return response.json();
  },

  // Vibe Check
  submitVibeCheck: async (data: { mood: string; energy_level: number; social_preference: number; music_taste: string[] }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/vibe/check`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        mood: data.mood,
        interests: data.music_taste,
      })
    });
    return response.json();
  },

  doVibeCheck: async (mood: string, energyLevel: number, eventId?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/vibe/check`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ mood, energy_level: energyLevel, event_id: eventId })
    });
    return response.json();
  },

  getVibeChecks: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/vibe/checks`, { headers });
    if (!response.ok) return [];
    return response.json();
  },

  // Memories
  createMemory: async (eventId: string, caption?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/memories?event_id=${eventId}${caption ? `&caption=${encodeURIComponent(caption)}` : ''}`,
      { method: 'POST', headers }
    );
    return response.json();
  },

  uploadMemoryMedia: async (memoryId: string, media: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/memories/${memoryId}/upload`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ media })
    });
    return response.json();
  },

  getEventMemories: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/memories/${eventId}`, { headers });
    return response.json();
  },

  getMyMemories: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/my-memories`, { headers });
    return response.json();
  },

  // Virtual Meetups
  createVirtualMeetup: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/virtual-meetups?event_id=${eventId}`,
      { method: 'POST', headers }
    );
    return response.json();
  },

  joinVirtualMeetup: async (roomCode: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/virtual-meetups/join/${roomCode}`,
      { method: 'POST', headers }
    );
    return response.json();
  },

  // Venue Meetups
  createVenueMeetup: async (eventId: string, location: { lat: number; lng: number }, lookingFor: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/venue-meetups`,
      { 
        method: 'POST', 
        headers, 
        body: JSON.stringify({ event_id: eventId, location, looking_for: lookingFor }) 
      }
    );
    return response.json();
  },

  getNearbyMeetups: async (eventId: string, lat: number, lng: number) => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/venue-meetups/${eventId}?lat=${lat}&lng=${lng}`,
      { headers }
    );
    return response.json();
  },

  // 2FA
  enable2FA: async (password: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/enable-2fa`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ password })
    });
    return response.json();
  },

  confirm2FA: async (code: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/confirm-2fa`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code })
    });
    return response.json();
  },

  disable2FA: async (code: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/disable-2fa`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code })
    });
    return response.json();
  },

  // Password Reset (SMS-based)
  forgotPassword: async (phoneNumber: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone_number: phoneNumber })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send reset code');
    }
    return response.json();
  },

  verifyResetCode: async (phoneNumber: string, code: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/verify-reset-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone_number: phoneNumber, code })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Invalid reset code');
    }
    return response.json();
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reset password');
    }
    return response.json();
  },

  // Seed data
  seedData: async () => {
    const response = await fetch(`${BACKEND_URL}/api/seed`, { method: 'POST' });
    return response.json();
  },

  // ===================== EVENT MANAGEMENT (HOSTS) =====================
  
  // Upload poster image
  uploadPoster: async (file: File): Promise<{ success: boolean; url: string; filename: string }> => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${BACKEND_URL}/api/upload/poster`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload poster');
    }
    return response.json();
  },

  createEvent: async (eventData: {
    title: string;
    description: string;
    artist: string;
    artist_ids?: string[];
    venue: string;
    location?: string;
    date: string;
    end_date?: string;
    category?: string;
    is_virtual?: boolean;
    stream_url?: string;
    tags?: string[];
    currency?: string;
    status?: string;
    poster_url?: string;
    ticket_tiers?: Array<{
      type_id?: string;
      name: string;
      price: number;
      description: string;
      perks?: string[];
      capacity: number;
    }>;
  }) => {
    const headers = await getAuthHeaders();
    
    // Transform ticket_tiers to include type_id if not present
    const transformedData = {
      ...eventData,
      location: eventData.location || eventData.venue, // Use venue as location if not provided
      ticket_tiers: (eventData.ticket_tiers || []).map((tier, index) => ({
        type_id: tier.type_id || `tier_${index}_${Date.now()}`,
        name: tier.name,
        price: tier.price,
        description: tier.description || '',
        perks: tier.perks || [],
        capacity: tier.capacity
      }))
    };
    
    const response = await fetch(`${BACKEND_URL}/api/events/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify(transformedData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create event');
    }
    return response.json();
  },

  updateEvent: async (eventId: string, eventData: any) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(eventData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update event');
    }
    return response.json();
  },

  publishEvent: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: 'published' })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to publish event');
    }
    return response.json();
  },

  unpublishEvent: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: 'draft' })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to unpublish event');
    }
    return response.json();
  },

  deleteEvent: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete event');
    }
    return response.json();
  },

  getMyEvents: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/my-events`, { headers });
    return response.json();
  },

  // ===================== STRIPE PAYMENTS =====================
  
  getStripePublishableKey: async () => {
    const response = await fetch(`${BACKEND_URL}/api/stripe/publishable-key`);
    return response.json();
  },

  calculatePrice: async (eventId: string, ticketType: string, quantity: number = 1) => {
    const response = await fetch(
      `${BACKEND_URL}/api/stripe/calculate-price?event_id=${eventId}&ticket_type=${ticketType}&quantity=${quantity}`
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to calculate price');
    }
    return response.json();
  },

  createCheckoutSession: async (eventId: string, ticketType: string, quantity: number = 1) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_id: eventId, ticket_type: ticketType, quantity })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create checkout session');
    }
    return response.json();
  },

  getCheckoutSession: async (sessionId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/stripe/checkout-session/${sessionId}`, { headers });
    return response.json();
  },

  verifyPayment: async (sessionId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/stripe/checkout-session/${sessionId}`, { headers });
    return response.json();
  },

  // ===================== CHAT MESSAGES (PERSISTENT) =====================
  
  getChatMessages: async (eventId: string, limit: number = 50, before?: string) => {
    const headers = await getAuthHeaders();
    let url = `${BACKEND_URL}/api/chat/${eventId}/messages?limit=${limit}`;
    if (before) url += `&before=${before}`;
    const response = await fetch(url, { headers });
    return response.json();
  },

  sendChatMessage: async (eventId: string, content: string, mediaUrl?: string, mediaType?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/chat/${eventId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_id: eventId, content, media_url: mediaUrl, media_type: mediaType })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send message');
    }
    return response.json();
  },

  deleteChatMessage: async (messageId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/chat/messages/${messageId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete message');
    }
    return response.json();
  },

  getChatParticipants: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/chat/${eventId}/participants`, { headers });
    return response.json();
  },

  // ===================== REFUND MANAGEMENT =====================
  
  getEventTickets: async (eventId: string, status?: string) => {
    const headers = await getAuthHeaders();
    let url = `${BACKEND_URL}/api/tickets/event/${eventId}`;
    if (status) url += `?status=${status}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get tickets');
    }
    return response.json();
  },

  processRefund: async (ticketId: string, reason?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/refunds/process`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ticket_id: ticketId, reason })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to process refund');
    }
    return response.json();
  },

  getRefundHistory: async (eventId?: string) => {
    const headers = await getAuthHeaders();
    let url = `${BACKEND_URL}/api/refunds/history`;
    if (eventId) url += `?event_id=${eventId}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get refund history');
    }
    return response.json();
  },

  // ===================== MEMORIES =====================
  
  createMemory: async (eventId: string, content: string, mediaUrls?: string[]) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/memories`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_id: eventId, content, media_urls: mediaUrls || [] })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create memory');
    }
    return response.json();
  },

  uploadMemoryMedia: async (memoryId: string, mediaUrl: string, mediaType: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/memories/${memoryId}/upload`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ media_url: mediaUrl, media_type: mediaType })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload media');
    }
    return response.json();
  },

  getEventMemories: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/memories/${eventId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get memories');
    }
    return response.json();
  },

  getMyMemories: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/my-memories`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get memories');
    }
    return response.json();
  },

  // ===================== VENUE MEETUPS =====================
  
  createVenueMeetup: async (eventId: string, title: string, description: string, location: { lat: number; lng: number }, meetTime: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/venue-meetups`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        event_id: eventId, 
        title, 
        description, 
        location,
        meet_time: meetTime
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create meetup');
    }
    return response.json();
  },

  getVenueMeetups: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/venue-meetups/${eventId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get meetups');
    }
    return response.json();
  },

  updateMeetupLocation: async (meetupId: string, location: { lat: number; lng: number }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/venue-meetups/${meetupId}/location`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ location })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update location');
    }
    return response.json();
  },

  // ===================== VIRTUAL MEETUPS =====================
  
  createVirtualMeetup: async (eventId: string, title: string, maxParticipants?: number) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/virtual-meetups`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        event_id: eventId, 
        title,
        max_participants: maxParticipants || 10
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create virtual meetup');
    }
    return response.json();
  },

  joinVirtualMeetup: async (roomCode: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/virtual-meetups/join/${roomCode}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to join room');
    }
    return response.json();
  },

  getVirtualMeetups: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/virtual-meetups/${eventId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get virtual meetups');
    }
    return response.json();
  },

  // ===================== LIVE STREAMING =====================
  
  createStream: async (eventId: string, title: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_id: eventId, title })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create stream');
    }
    return response.json();
  },

  startStream: async (streamId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/${streamId}/start`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start stream');
    }
    return response.json();
  },

  endStream: async (streamId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/${streamId}/end`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to end stream');
    }
    return response.json();
  },

  getActiveStreams: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/event/${eventId}`, { headers });
    if (!response.ok) {
      return []; // Return empty array if no streams
    }
    return response.json();
  },

  joinStream: async (streamId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/${streamId}/join`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to join stream');
    }
    return response.json();
  },

  leaveStream: async (streamId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/${streamId}/leave`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to leave stream');
    }
    return response.json();
  },

  // ===================== MEETUP ROOMS =====================

  createMeetupRoom: async (data: {
    event_id: string;
    title: string;
    description?: string;
    max_participants?: number;
  }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/meetup`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create meetup room');
    }
    return response.json();
  },

  getEventMeetupRooms: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/meetup/event/${eventId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get meetup rooms');
    }
    return response.json();
  },

  joinMeetupRoom: async (meetupId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/meetup/${meetupId}/join`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to join meetup room');
    }
    return response.json();
  },

  leaveMeetupRoom: async (meetupId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/meetup/${meetupId}/leave`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to leave meetup room');
    }
    return response.json();
  },

  endMeetupRoom: async (meetupId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/streams/meetup/${meetupId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to end meetup room');
    }
    return response.json();
  },

  // ===================== ARTIST PROFILE =====================

  getMyArtistProfile: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/me`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'You are not an artist');
    }
    return response.json();
  },

  searchArtists: async (query: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists?search=${encodeURIComponent(query)}`, { headers });
    if (!response.ok) {
      return [];
    }
    return response.json();
  },

  // ===================== LOCATION SHARING =====================
  
  updateLocation: async (lat: number, lng: number, eventId?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/location/update`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ lat, lng, event_id: eventId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update location');
    }
    return response.json();
  },

  getNearbyUsers: async (eventId: string, radius?: number) => {
    const headers = await getAuthHeaders();
    let url = `${BACKEND_URL}/api/location/nearby?event_id=${eventId}`;
    if (radius) url += `&radius=${radius}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get nearby users');
    }
    return response.json();
  },

  shareLocation: async (eventId: string, duration: number = 3600) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/location/share`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_id: eventId, duration })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to share location');
    }
    return response.json();
  },

  // ===================== PUSH NOTIFICATIONS =====================
  
  registerPushToken: async (token: string, platform: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/push-token`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token, platform })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to register push token');
    }
    return response.json();
  },

  removePushToken: async (token: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/push-token`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ token })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove push token');
    }
    return response.json();
  },

  // ===================== FRIENDS SYSTEM =====================
  
  getFriends: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/friends`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get friends');
    }
    return response.json();
  },

  getFriendRequests: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/friends/requests`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get friend requests');
    }
    return response.json();
  },

  sendFriendRequest: async (friendId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/friends/request?friend_id=${friendId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send friend request');
    }
    return response.json();
  },

  respondToFriendRequest: async (friendshipId: string, accept: boolean) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/friends/respond?friendship_id=${friendshipId}&accept=${accept}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to respond to friend request');
    }
    return response.json();
  },

  removeFriend: async (friendId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/friends/${friendId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove friend');
    }
    return response.json();
  },

  getFriendsEvents: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/friends/events`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get friends events');
    }
    return response.json();
  },

  searchUsers: async (query: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/users/search?query=${encodeURIComponent(query)}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to search users');
    }
    return response.json();
  },

  // ===================== TICKET VERIFICATION =====================
  
  verifyTicket: async (ticketId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/tickets/${ticketId}/verify`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to verify ticket');
    }
    return response.json();
  },

  checkInTicket: async (ticketId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/tickets/${ticketId}/check-in`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to check in ticket');
    }
    return response.json();
  },

  // ===================== HOST ANALYTICS =====================
  
  getAnalyticsOverview: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analytics/overview`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get analytics');
    }
    return response.json();
  },

  getEventAnalytics: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/analytics/event/${eventId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get event analytics');
    }
    return response.json();
  },

  // ===================== AGORA VIDEO/STREAMING =====================
  
  getAgoraConfig: async () => {
    const response = await fetch(`${BACKEND_URL}/api/agora/config`);
    return response.json();
  },

  getAgoraToken: async (channelName: string, uid: number, role: 'publisher' | 'subscriber' = 'publisher', expirationSeconds: number = 3600) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/agora/token`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        channel_name: channelName,
        uid,
        role,
        expiration_seconds: expirationSeconds
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get Agora token');
    }
    return response.json();
  },

  // ===================== POLLS / VOTING =====================
  
  createPoll: async (eventId: string, question: string, options: string[], allowMultiple: boolean = false, endsAt?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        event_id: eventId,
        question,
        options,
        allow_multiple: allowMultiple,
        ends_at: endsAt
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create poll');
    }
    return response.json();
  },

  getEventPolls: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/event/${eventId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get polls');
    }
    return response.json();
  },

  getPoll: async (pollId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get poll');
    }
    return response.json();
  },

  voteOnPoll: async (pollId: string, optionIndices: number[]) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}/vote`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ option_indices: optionIndices })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to vote');
    }
    return response.json();
  },

  endPoll: async (pollId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}/end`, {
      method: 'PUT',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to end poll');
    }
    return response.json();
  },

  deletePoll: async (pollId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete poll');
    }
    return response.json();
  },

  // ===================== SUPPORT TICKETS =====================

  submitSupportTicket: async (data: { subject: string; message: string; category: string; user_email: string; user_name: string }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/support/tickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit support ticket');
    }
    return response.json();
  },

  getSupportTickets: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/support/tickets`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get support tickets');
    }
    return response.json();
  },

  updateSupportTicket: async (ticketId: string, status: string, response?: string) => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${BACKEND_URL}/api/support/tickets/${ticketId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status, admin_response: response })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to update ticket');
    }
    return res.json();
  },

  // ===================== NOTIFICATION SETTINGS =====================

  updateNotificationSettings: async (settings: any) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/users/notification-settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update notification settings');
    }
    return response.json();
  },

  getNotificationSettings: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/users/notification-settings`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get notification settings');
    }
    return response.json();
  },

  // ===================== ARTISTS =====================

  getArtists: async (search?: string, genre?: string) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (genre) params.append('genre', genre);
    const response = await fetch(`${BACKEND_URL}/api/artists?${params.toString()}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get artists');
    }
    return response.json();
  },

  getArtist: async (artistId: string) => {
    const response = await fetch(`${BACKEND_URL}/api/artists/${artistId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get artist');
    }
    return response.json();
  },

  createArtistProfile: async (stageName: string, bio?: string, genre?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ stage_name: stageName, bio, genre })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create artist profile');
    }
    return response.json();
  },

  followArtist: async (artistId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/${artistId}/follow`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to follow artist');
    }
    return response.json();
  },

  unfollowArtist: async (artistId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/${artistId}/follow`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to unfollow artist');
    }
    return response.json();
  },

  // Artist Request & Approval
  requestArtistProfile: async (data: {
    stage_name: string;
    bio?: string;
    genres: string[];
    profile_image?: string;
    social_links?: Record<string, string>;
  }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/request`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit artist request');
    }
    return response.json();
  },

  getMyArtistRequest: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/my-request`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get artist request status');
    }
    return response.json();
  },

  getArtistRequests: async (status?: string) => {
    const headers = await getAuthHeaders();
    const query = status ? `?status=${status}` : '';
    const response = await fetch(`${BACKEND_URL}/api/artists/requests${query}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get artist requests');
    }
    return response.json();
  },

  approveArtistRequest: async (requestId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/requests/${requestId}/approve`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to approve artist request');
    }
    return response.json();
  },

  rejectArtistRequest: async (requestId: string, reason?: string) => {
    const headers = await getAuthHeaders();
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await fetch(`${BACKEND_URL}/api/artists/requests/${requestId}/reject${query}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reject artist request');
    }
    return response.json();
  },

  getArtistGenres: async () => {
    const response = await fetch(`${BACKEND_URL}/api/artists/genres`);
    return response.json();
  },

  getArtistsByGenre: async (genre: string) => {
    const response = await fetch(`${BACKEND_URL}/api/artists/by-genre/${encodeURIComponent(genre)}`);
    return response.json();
  },

  getArtistEvents: async (artistId: string) => {
    const response = await fetch(`${BACKEND_URL}/api/artists/${artistId}/events`);
    return response.json();
  },

  isFollowingArtist: async (artistId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artists/${artistId}/is-following`, { headers });
    if (!response.ok) return { is_following: false };
    return response.json();
  },

  adminCreateArtist: async (data: {
    stage_name: string;
    bio?: string;
    genres: string[];
    profile_image?: string;
    social_links?: Record<string, string>;
  }, userId?: string) => {
    const headers = await getAuthHeaders();
    const query = userId ? `?user_id=${userId}` : '';
    const response = await fetch(`${BACKEND_URL}/api/admin/artists${query}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create artist');
    }
    return response.json();
  },

  // QR Ticket Scanning
  scanTicket: async (eventId: string, ticketId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/scan-ticket?ticket_id=${ticketId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to scan ticket');
    }
    return response.json();
  },

  getEventScanStats: async (eventId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/events/${eventId}/scan-stats`, { headers });
    if (!response.ok) {
      return { total: 0, scanned: 0, remaining: 0 };
    }
    return response.json();
  },

  verifyTicket: async (ticketId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/tickets/${ticketId}/verify`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to verify ticket');
    }
    return response.json();
  },

  checkInTicket: async (ticketId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/tickets/${ticketId}/check-in`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to check in ticket');
    }
    return response.json();
  },

  // ===================== CONFIG & GUIDELINES =====================
  
  getAppConfig: async () => {
    const response = await fetch(`${BACKEND_URL}/api/config`);
    if (!response.ok) throw new Error('Failed to get app config');
    return response.json();
  },

  getCommunityGuidelines: async () => {
    const response = await fetch(`${BACKEND_URL}/api/community-guidelines`);
    if (!response.ok) throw new Error('Failed to get community guidelines');
    return response.json();
  },

  getSupportedCurrencies: async () => {
    const response = await fetch(`${BACKEND_URL}/api/config/currencies`);
    if (!response.ok) throw new Error('Failed to get currencies');
    return response.json();
  },

  getEventCategories: async () => {
    const response = await fetch(`${BACKEND_URL}/api/config/categories`);
    if (!response.ok) throw new Error('Failed to get categories');
    return response.json();
  },

  // ===================== HOST APPLICATIONS =====================
  
  submitHostApplication: async (data: {
    business_name: string;
    description: string;
    social_links?: { [key: string]: string };
    website?: string;
    experience?: string;
    event_types?: string[];
  }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/host-applications`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    
    const responseText = await response.text();
    
    if (!response.ok) {
      let errorMessage = 'Failed to submit application';
      try {
        const error = JSON.parse(responseText);
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        errorMessage = responseText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    try {
      return JSON.parse(responseText);
    } catch (e) {
      throw new Error('Invalid response from server');
    }
  },

  getMyHostApplication: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/host-applications/my`, { headers });
    
    const responseText = await response.text();
    
    if (!response.ok) {
      let errorMessage = 'Failed to get application status';
      try {
        const error = JSON.parse(responseText);
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        errorMessage = responseText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    try {
      return JSON.parse(responseText);
    } catch (e) {
      // Return default state if response is not valid JSON
      return { status: null, is_host: false, application: null };
    }
  },

  getHostRequests: async (status: string = 'pending') => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/admin/host-requests?status=${status}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get host requests');
    }
    return response.json();
  },

  approveHostRequest: async (applicationId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/admin/host-requests/${applicationId}/approve`, {
      method: 'PUT',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to approve application');
    }
    return response.json();
  },

  rejectHostRequest: async (applicationId: string, reason?: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/admin/host-requests/${applicationId}/reject?reason=${encodeURIComponent(reason || '')}`, {
      method: 'PUT',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reject application');
    }
    return response.json();
  },

  // ===================== SMS 2FA =====================
  
  setup2FASMS: async (password: string, phoneNumber: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/setup-sms`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ password, phone_number: phoneNumber })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to setup SMS 2FA');
    }
    return response.json();
  },

  confirm2FASMS: async (code: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/confirm-sms`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to confirm SMS 2FA');
    }
    return response.json();
  },

  send2FASMS: async (userId: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/send-sms?user_id=${userId}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send SMS code');
    }
    return response.json();
  },

  verify2FASMS: async (userId: string, code: string) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/verify-sms?user_id=${userId}&code=${code}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Invalid code');
    }
    return response.json();
  },

  get2FAStatus: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/status`, { headers });
    if (!response.ok) throw new Error('Failed to get 2FA status');
    return response.json();
  },

  switch2FAMethod: async (method: 'totp' | 'sms', phoneNumber?: string) => {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ method });
    if (phoneNumber) params.append('phone_number', phoneNumber);
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/switch-method?${params}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to switch method');
    }
    return response.json();
  },

  confirmSwitch2FA: async (code: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/auth/2fa/confirm-switch`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ code })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to confirm switch');
    }
    return response.json();
  },

  // ===================== UPCOMING EVENTS =====================
  
  getMyUpcomingEvents: async (limit: number = 10) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/my-upcoming-events?limit=${limit}`, { headers });
    if (!response.ok) throw new Error('Failed to get upcoming events');
    return response.json();
  },

  // ===================== ADMIN POLLS =====================
  
  getAdminPolls: async (status?: string) => {
    const headers = await getAuthHeaders();
    const params = status ? `?status=${status}` : '';
    const response = await fetch(`${BACKEND_URL}/api/admin/polls${params}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get polls');
    }
    return response.json();
  },

  getAdminPollsStats: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/admin/polls/stats`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get poll stats');
    }
    return response.json();
  },

  endPoll: async (pollId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}/end`, {
      method: 'PUT',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to end poll');
    }
    return response.json();
  },

  deletePoll: async (pollId: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete poll');
    }
    return response.json();
  },

  // ===================== HOST APPLICATIONS =====================

  applyForHost: async (data: {
    business_name: string;
    business_type?: string;
    experience?: string;
    why_host: string;
    social_links?: string;
  }) => {
    const headers = await getAuthHeaders();
    // Map form fields to backend expected fields
    const payload = {
      business_name: data.business_name,
      description: data.why_host, // Map why_host to description
      experience: data.experience || '',
      event_types: data.business_type ? [data.business_type] : [],
      social_links: data.social_links ? { other: data.social_links } : {}
    };
    const response = await fetch(`${BACKEND_URL}/api/host-applications`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit application');
    }
    return response.json();
  },

  getMyHostApplication: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/host-applications/my`, { headers });
    if (!response.ok) {
      return null;
    }
    return response.json();
  },

  // ===================== ARTIST APPLICATIONS =====================

  applyForArtist: async (data: {
    stage_name: string;
    bio: string;
    genres?: string;
    experience?: string;
    social_links?: string;
    portfolio_url?: string;
  }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artist-applications`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit application');
    }
    return response.json();
  },

  getMyArtistApplication: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/artist-applications/my`, { headers });
    if (!response.ok) {
      return null;
    }
    return response.json();
  },

  getHostApplications: async (status: string = 'pending') => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/admin/host-requests?status=${status}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get host applications');
    }
    return response.json();
  },

  reviewHostApplication: async (applicationId: string, action: 'approve' | 'reject', reason?: string) => {
    const headers = await getAuthHeaders();
    const url = `${BACKEND_URL}/api/admin/host-requests/${applicationId}/${action}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ reason })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to ${action} application`);
    }
    return response.json();
  },

  // ===================== ARTIST APPLICATIONS =====================

  getArtistApplications: async (status: string = 'pending') => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BACKEND_URL}/api/admin/artist-requests?status=${status}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get artist applications');
    }
    return response.json();
  },

  reviewArtistApplication: async (applicationId: string, action: 'approve' | 'reject', reason?: string) => {
    const headers = await getAuthHeaders();
    const url = `${BACKEND_URL}/api/admin/artist-requests/${applicationId}/${action}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ reason })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to ${action} application`);
    }
    return response.json();
  }
};
